import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Star, Bell, Building2, ChevronDown, ChevronRight, Plus, Trash2, ShieldCheck, PanelLeftClose } from 'lucide-react';
import { useChatSession } from '../../contexts/ChatSessionContext';
import { useState, useEffect } from 'react';
import Logo from '../shared/Logo';
import { getAllCollections } from '../../utils/institutions';

const OLDER_OPEN_KEY = 'alertai.sidebar.anterioresAbiertas';

/** Separa las conversaciones de hoy (día calendario local) de las anteriores, respetando el orden recibido. */
function splitByToday(sessions: any[]) {
  const today = new Date().toDateString();
  const todays: any[] = [];
  const older: any[] = [];
  for (const s of sessions) {
    (new Date(s.updated_at || s.created_at).toDateString() === today ? todays : older).push(s);
  }
  return { todays, older };
}

function readOlderOpen() {
  try { return localStorage.getItem(OLDER_OPEN_KEY) === '1'; } catch { return false; }
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: { collapsed: boolean; onToggle: () => void; mobileOpen: boolean; onMobileClose: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const chatSession = useChatSession();
  // «Anteriores» parte plegado y recuerda la elección del usuario (pedido del dueño, 2026-10-08).
  const [olderOpen, setOlderOpen] = useState(readOlderOpen);
  const toggleOlder = () => {
    setOlderOpen(open => {
      try { localStorage.setItem(OLDER_OPEN_KEY, open ? '0' : '1'); } catch { /* sin almacenamiento */ }
      return !open;
    });
  };

  const collections = getAllCollections();
  // La barra es igual en todas las pestañas (D-UI-2): las conversaciones se piden al montarla.
  // Depende de la función (estable) y no del objeto del contexto, que cambia con cada lista (R-11).
  const refreshSessions = chatSession?.refreshSessions;
  useEffect(() => {
    refreshSessions?.();
  }, [refreshSessions]);

  // '/busqueda-inteligente' redirige a '/chat'; también cuenta como el chat mientras se resuelve.
  const isChatRoute = location.pathname === '/chat' || location.pathname === '/busqueda-inteligente';
  const inLibrary = location.pathname.startsWith('/biblioteca');

  // Fuera del chat, elegir una conversación lleva al chat y la abre (el contexto la deja pendiente hasta que monte).
  const handleSelectSession = (id: string) => {
    chatSession?.onSelectSession(id);
    if (!isChatRoute) navigate('/chat');
  };

  const handleNewConversation = () => {
    chatSession?.onNewSession();
    navigate('/chat');
    onMobileClose();
  };

  const navigation = [
    { name: 'Favoritos', href: '/favoritos', icon: Star },
    { name: 'Alertas', href: '/notificaciones', icon: Bell },
    { name: 'Cobertura', href: '/cobertura', icon: ShieldCheck },
  ];

  const isActive = (path: string) => location.pathname === path;

  const { todays, older } = chatSession ? splitByToday(chatSession.sessions) : { todays: [], older: [] };
  // Con «Anteriores» plegado, la conversación abierta igual se ve si es de otro día.
  const olderShown = olderOpen ? older : older.filter(s => s.id === chatSession?.currentSessionId);

  const renderSession = (session: any) => {
    const isSessionActive = session.id === chatSession?.currentSessionId;
    return (
      <button
        key={session.id}
        onClick={() => handleSelectSession(session.id)}
        className={`fila-presion w-full text-left px-2 py-1.5 rounded-lg mb-0.5 group transition-colors ${
          isSessionActive
            ? 'bg-indigo-50 border-l-[3px] border-indigo-500'
            : 'hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center justify-between">
          <p className={`text-[13px] truncate flex-1 ${
            isSessionActive ? 'font-medium text-gray-900' : 'text-gray-700'
          }`}>
            {session.title || 'Sin título'}
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              chatSession?.onDeleteSession(session.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-all ml-1"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </button>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out motion-reduce:transition-none ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} ${collapsed ? 'lg:-translate-x-full' : 'lg:translate-x-0'}`}
      >
        <div className="flex flex-col h-full">
          <div className="h-[85px] shrink-0 pl-6 pr-3 border-b border-gray-200 flex items-center justify-between">
            {/* El logo lleva al inicio del chat: mismo efecto que «Nueva conversación». */}
            <button type="button" onClick={handleNewConversation} aria-label="Ir al inicio del chat" className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300">
              <Logo variant="compact" />
            </button>
            <button onClick={onToggle} className="hidden lg:inline-flex p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50" aria-label="Ocultar barra lateral" title="Ocultar barra lateral (Ctrl+B)">
              <PanelLeftClose className="w-4 h-4" />
            </button>
          </div>

          {/* Always-visible new conversation entry point — chat is the app home */}
          <div className="px-3 pt-4 flex-shrink-0">
            <button
              onClick={handleNewConversation}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nueva conversación
            </button>
          </div>

          {/* Middle scroll region: session history + main nav */}
          <div className="flex-1 overflow-y-auto min-h-0 px-3 py-4">
            {chatSession && chatSession.sessions.length > 0 && (
              <div className="pb-3 mb-3 border-b border-gray-200">
                {todays.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-slate-500 px-2 pt-1 pb-1">Hoy</p>
                    {todays.map(renderSession)}
                  </>
                )}
                {older.length > 0 && (
                  <>
                    <button
                      onClick={toggleOlder}
                      aria-expanded={olderOpen}
                      aria-label={`Anteriores, ${older.length}`}
                      className="w-full flex items-center gap-1 px-2 pt-2.5 pb-1 text-xs font-medium text-slate-500 hover:text-slate-700"
                    >
                      {olderOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Anteriores · <span className="tabular-nums">{older.length}</span>
                    </button>
                    {olderShown.map(renderSession)}
                  </>
                )}
              </div>
            )}

            <ul className="space-y-1">
              {/* Bibliotecas: enlace al índice; las 4 colecciones se despliegan solo dentro de una biblioteca */}
              <li>
                <Link
                  to="/bibliotecas"
                  onClick={onMobileClose}
                  className={`presion flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${inLibrary
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Building2 className="w-5 h-5" />
                  Bibliotecas
                </Link>

                {inLibrary && (
                  <ul className="mt-1 ml-[22px] pl-2.5 space-y-0.5 border-l border-gray-200">
                    {collections.map((collection) => {
                      const active = location.pathname === `/biblioteca/${collection.key}`;
                      return (
                        <li key={collection.key}>
                          {/* Con Inter, «Jurisprudencia administrativa» mide 181 px (184 en negrita) y con px-2.5 solo había 178: pl-2 pr-1.5 deja 184 y la activa se marca por color y fondo, sin negrita, para que quepa en una línea con aire a la derecha */}
                          <Link
                            to={`/biblioteca/${collection.key}`}
                            onClick={onMobileClose}
                            aria-current={active ? 'page' : undefined}
                            className={`presion flex items-center gap-2 pl-2 pr-1.5 py-1.5 rounded-lg text-[13px] transition-colors ${active
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                          >
                            <span>{collection.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>

              {/* Favoritos, Alertas, Cobertura */}
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={onMobileClose}
                      className={`presion flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={onMobileClose}
        />
      )}
    </>
  );
}
